import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { DollarSign, Search, X, Save, ChevronRight, AlertTriangle, Upload } from 'lucide-react';
import { statusLabel } from './statusLabels';
import { API_BASE } from '../../config';
import theme from '../../theme';

// Evidence comes back as `/uploads/...`, which a browser resolves against
// whatever origin served the page. Under nginx that is the backend, but the
// Capacitor build has no such origin — the page is a file inside the app bundle
// — so an unprefixed path silently resolves to nothing. Same helper as Issues.js,
// derived from the one place the API location is configured.
const UPLOAD_BASE = API_BASE.replace(/\/api\/?$/, '');
const photoUrl = (u = '') =>
  /^https?:\/\//i.test(u) ? u : `${UPLOAD_BASE}${u.startsWith('/') ? '' : '/'}${u}`;

// What POST /rentals/:id/dispute/evidence accepts, mirrored so the picker does
// not offer a file the server will refuse.
const MAX_EVIDENCE_PHOTOS = 6;

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

const STATUS_COLORS = {
  pending_payment: theme.state.pending,
  paid:            theme.state.success,
  picked_up:       theme.state.info,
  returned:        theme.state.neutral,
  late:            theme.state.danger,
  cancelled:       theme.state.muted,
  disputed:        theme.state.dispute,
  lost:            theme.state.lost,
};

// Which status buttons to show per current status.
//
// 'disputed' is deliberately absent: the API refuses to set it through
// PATCH /status and answers 400, because a dispute is a record (reason, amount,
// appeal deadline) rather than a label. It is opened with the button below and
// left again by resolving it — patching a disputed rental straight back to
// 'returned' would strand an open claim and let the deposit refund slip past it.
const STATUS_TRANSITIONS = {
  pending_payment: ['cancelled'],
  paid:            ['picked_up', 'cancelled'],
  picked_up:       ['returned', 'late'],
  late:            ['returned'],
  returned:        [],
  cancelled:       [],
  disputed:        [],
  // Terminal. The API answers 409 RENTAL_WRITTEN_OFF on any transition out of
  // it: the deposit has been forfeited and invoiced, so reopening the rental
  // would put an already-booked payment back in front of the refund path.
  lost:            [],
};

const TRANSITION_LABEL_KEYS = {
  picked_up: 'markCollected',
  returned:  'markReturned',
  late:      'markLate',
  cancelled: 'cancelRental',
};

const TRANSITION_COLORS = {
  picked_up: theme.blue,
  returned:  theme.green,
  late:      theme.amber,
  cancelled: theme.red,
};

// The refund endpoints answer 400/409 with a `message` that names the actual
// obstacle — an open chargeback, nothing paid to give back, or the maximum the
// payment can still return. The axios interceptor flattens the body, so `error`
// is the machine code and `message` is the sentence worth showing.
const serverText = (err, fallback) =>
  (err && typeof err === 'object' && err.error) ? (err.message || err.error) : fallback;

const DISPUTE_STATUS_KEYS = {
  open:     'disputeStateOpen',
  appealed: 'disputeStateAppealed',
  upheld:   'disputeStateUpheld',
  waived:   'disputeStateWaived',
};

export default function AdminRentals() {
  const { t } = useTranslation();
  const [rentals, setRentals]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]             = useState('');
  // The box the admin types in vs. the value actually sent. Filtering happens on
  // the server now (see loadRentals), so every keystroke would otherwise be a
  // request.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [detail, setDetail]             = useState(null);
  const [refundModal, setRefundModal]   = useState(null);
  const [deduction, setDeduction]       = useState('0');
  const [deductionReason, setDeductionReason] = useState('');
  // Refunding the RENTAL price, which is a different endpoint and a different
  // pot of money from the deposit refund above. Kept as its own modal so the two
  // can never be confused at the moment of clicking.
  const [rentalRefundModal, setRentalRefundModal]   = useState(null);
  const [rentalRefundAmount, setRentalRefundAmount] = useState('');
  const [rentalRefundReason, setRentalRefundReason] = useState('');
  const [notes, setNotes]               = useState('');
  const [damage, setDamage]             = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  // `actionLoading` is state, so `disabled={actionLoading}` only lands on the
  // next render: two taps in the same tick — routine on a touchscreen — both
  // read the stale false and both fire. The refunds happen to be safe because
  // the server guards them under FOR UPDATE, but opening a dispute and moving a
  // status are not, and neither is anything added here later. One synchronous
  // gate in front of every action, the same fix Checkout's payingRef was.
  const actionRef = useRef(false);
  const beginAction = () => {
    if (actionRef.current) return false;
    actionRef.current = true;
    setActionLoading(true);
    return true;
  };
  const endAction = () => {
    actionRef.current = false;
    setActionLoading(false);
  };
  const [disputes, setDisputes]         = useState([]);
  const [disputeModal, setDisputeModal] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeAmount, setDisputeAmount] = useState('');
  const [resolveFinal, setResolveFinal]   = useState('');
  const [resolveNotes, setResolveNotes]   = useState('');
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [lostModal, setLostModal]         = useState(null);
  const [lostReason, setLostReason]       = useState('');
  const [lostForfeit, setLostForfeit]     = useState('');
  const [page, setPage]                 = useState(1);
  const [totalCount, setTotalCount]     = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // A new query has to start at page 1: staying on page 4 of the old result set
  // would ask the server for a page that the narrower query may not have.
  useEffect(() => { setPage(1); }, [statusFilter, debouncedSearch]);
  useEffect(() => { loadRentals(); }, [statusFilter, page, debouncedSearch]);

  const loadRentals = () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: PAGE_SIZE });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    // Server-side. Filtering the 50 rows of the current page client-side meant a
    // customer phoning from the locker could not be found unless they happened
    // to be in the newest 50 rentals — and the header then claimed "0 results"
    // as though they did not exist. The server matches name, email, phone and
    // product name, and still answers a bare array plus X-Total-Count.
    if (debouncedSearch) params.set('search', debouncedSearch);
    const token = localStorage.getItem('glamgo_token');
    const base = process.env.REACT_APP_API_URL || '/api';
    // Raw axios on purpose: the shared `api` instance unwraps to res.data, and
    // this table needs the x-total-count header for pagination. The cost is that
    // it also bypasses the 401 handling in that instance's interceptor, so an
    // admin with an expired token used to sit in front of a permanently empty
    // table with no error and no redirect. Both are handled explicitly here.
    axios.get(`${base}/rentals?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setRentals(Array.isArray(res.data) ? res.data : []);
        setTotalCount(parseInt(res.headers['x-total-count'] || '0', 10));
      })
      .catch(err => {
        if (err.response?.status === 401) {
          localStorage.removeItem('glamgo_token');
          localStorage.removeItem('glamgo_user');
          window.location.href = '/login';
          return;
        }
        toast.error(t('admin.loadError'));
      })
      .finally(() => setLoading(false));
  };

  const loadDisputes = (rentalId) =>
    api.get(`/rentals/${rentalId}/dispute`)
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        setDisputes(list);
        const live = list.find(x => ['open', 'appealed'].includes(x.status));
        setResolveFinal(live ? parseFloat(live.amount_claimed).toFixed(2) : '');
        return list;
      })
      .catch(() => { setDisputes([]); return []; });

  const openDetail = (r) => {
    setDetail(r);
    setNotes(r.admin_notes || '');
    setDamage(r.damage_description || '');
    setDisputes([]);
    setResolveNotes('');
    setResolveFinal('');
    loadDisputes(r.id);
  };

  const closeDetail = () => {
    setDetail(null); setNotes(''); setDamage('');
    setDisputes([]); setResolveNotes(''); setResolveFinal('');
  };

  const updateStatus = useCallback(async (id, status, extra = {}) => {
    // Inlined rather than calling beginAction(): this one is a useCallback, and
    // closing over a helper recreated every render is exactly the kind of stale
    // reference the guard exists to avoid.
    if (actionRef.current) return;
    actionRef.current = true;
    setActionLoading(true);
    try {
      const { warning, message, ...updated } = await api.patch(`/rentals/${id}/status`, { status, ...extra });
      toast.success(t('admin.rentals.statusChanged', { status: statusLabel(t, status) }));
      // Cancelling a rental that was already paid changes the row and nothing
      // else — no money moves. Silence here left the admin believing the customer
      // had been made whole, which is exactly the case the Terms promise a full
      // refund for. Say it, and point at the action that actually does it.
      if (warning === 'REFUND_OWED') {
        toast(t('admin.rentals.refundOwedWarning'), { icon: '⚠️', duration: 14000 });
      }
      setDetail(prev => prev?.id === id ? { ...prev, ...updated } : prev);
      loadRentals();
    } catch (err) {
      toast.error(serverText(err, t('admin.rentals.failed')), { duration: 8000 });
    } finally {
      actionRef.current = false;
      setActionLoading(false);
    }
  }, [t]);

  const saveNotes = async () => {
    if (!detail) return;
    if (!beginAction()) return;
    try {
      // No status. Sending the current one made this a status transition as far
      // as the server was concerned, and it re-ran the whole branch: saving a
      // note on a returned rental flipped the product back to 'cleaning' and
      // restarted the 48-hour deposit clock. It also meant notes could not be
      // saved at all on a disputed rental, since 'disputed' is not a status this
      // endpoint accepts — the rentals that most need a note written on them.
      const updated = await api.patch(`/rentals/${detail.id}/status`, {
        admin_notes: notes,
        damage_description: damage,
      });
      toast.success(t('admin.rentals.notesSaved'));
      setDetail(prev => ({ ...prev, ...updated }));
      loadRentals();
    } catch (err) {
      toast.error(err.error || t('admin.rentals.notesSaveFailed'));
    } finally {
      endAction();
    }
  };

  const handleOpenDispute = async () => {
    if (!disputeModal) return;
    if (!disputeReason.trim()) return toast.error(t('admin.rentals.disputeReasonRequired'));
    if (!beginAction()) return;
    try {
      const created = await api.post(`/rentals/${disputeModal.id}/dispute`, {
        reason: disputeReason.trim(),
        amount_claimed: parseFloat(disputeAmount) || 0,
      });
      setDisputes(prev => [created, ...prev]);
      setResolveFinal(parseFloat(created.amount_claimed).toFixed(2));
      setDetail(prev => (prev?.id === disputeModal.id ? { ...prev, status: 'disputed' } : prev));
      setDisputeModal(null);
      setDisputeReason('');
      setDisputeAmount('');
      toast.success(t('admin.rentals.disputeOpenedToast'));
      loadRentals();
    } catch (err) {
      toast.error(err.error || t('admin.rentals.disputeOpenFailed'));
    } finally {
      endAction();
    }
  };

  const handleResolve = async (outcome) => {
    if (!detail) return;
    if (!beginAction()) return;
    try {
      const body = { outcome, resolution_notes: resolveNotes.trim() || undefined };
      // Only 'upheld' keeps money, and an empty box means "the amount claimed" —
      // the server applies exactly that fallback, so don't invent a 0 here.
      if (outcome === 'upheld' && String(resolveFinal).trim() !== '') {
        body.final_amount = parseFloat(resolveFinal) || 0;
      }
      const result = await api.post(`/rentals/${detail.id}/dispute/resolve`, body);
      setDisputes(prev => [result.dispute, ...prev.filter(d => d.id !== result.dispute.id)]);
      setDetail(prev => (prev ? { ...prev, status: 'returned' } : prev));
      toast.success(t('admin.rentals.disputeResolvedToast'));
      loadRentals();
    } catch (err) {
      toast.error(err.error || t('admin.rentals.disputeResolveFailed'));
    } finally {
      endAction();
    }
  };

  // Photographs attached to a live dispute.
  //
  // POST /rentals/:id/dispute/evidence has existed since disputes did — six
  // photos, admin-only, appended to the dispute row — and nothing ever called
  // it. That matters twice over: the damage claim we put to the customer had
  // nothing behind it but a sentence in a text box, and if they answer it with
  // a card chargeback, photographs of the returned item are precisely what
  // Stripe asks us to produce and what we had no way to store.
  //
  // Deliberately not routed through `beginAction`: that guard exists to stop two
  // money-moving actions racing, and an upload neither moves money nor changes
  // status. Blocking it behind the same latch would mean a slow upload locked
  // the resolve buttons.
  const handleEvidenceUpload = async (rentalId, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > MAX_EVIDENCE_PHOTOS) {
      return toast.error(t('admin.rentals.evidenceTooMany', { max: MAX_EVIDENCE_PHOTOS }));
    }
    setEvidenceUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('photos', f));
      const token = localStorage.getItem('glamgo_token');
      // Raw fetch because the shared `api` instance sets a JSON content type,
      // which would strip the multipart boundary multer needs.
      const res = await fetch(`${API_BASE}/rentals/${rentalId}/dispute/evidence`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      // Raw fetch, so the interceptor never sees this — read the body here or
      // the reason (unsupported type, too large, no open dispute) is lost and
      // the operator gets a bare "upload failed" for all of them.
      if (!res.ok) {
        let msg = t('admin.rentals.evidenceFailed');
        try {
          const body = await res.json();
          if (body?.message || body?.error) msg = body.message || body.error;
        } catch { /* non-JSON error body; keep the fallback */ }
        throw new Error(msg);
      }
      const updated = await res.json();
      setDisputes(prev => prev.map(d => (d.id === updated.id ? { ...d, ...updated } : d)));
      toast.success(t('admin.rentals.evidenceUploaded', { count: files.length }));
    } catch (err) {
      toast.error(err.message || t('admin.rentals.evidenceFailed'), { duration: 8000 });
    } finally {
      setEvidenceUploading(false);
    }
  };

  const handleRefund = async () => {
    if (!refundModal) return;
    if (!beginAction()) return;
    try {
      // An empty box is not zero. Omitting deduction_amount lets the server fall
      // back to the amount agreed when the dispute was resolved; sending 0 would
      // override that and hand back the whole deposit.
      const body = { reason: deductionReason };
      if (String(deduction).trim() !== '') body.deduction_amount = parseFloat(deduction) || 0;

      const result = await api.post(`/rentals/${refundModal.id}/refund-deposit`, body);
      // Raw interpolation rendered a €192.30 refund as "€192.3".
      toast.success(t('admin.rentals.depositRefundedToast', { amount: (parseFloat(result.refunded) || 0).toFixed(2) }));
      setRefundModal(null);
      setDeduction('0');
      setDeductionReason('');
      if (detail?.id === refundModal.id) closeDetail();
      loadRentals();
    } catch (err) {
      // 409 covers "already refunded" and "a dispute is still open". Re-read the
      // disputes rather than guessing from stale state — another admin may have
      // opened one since this modal was rendered.
      if (err.status === 409) {
        const fresh = await loadDisputes(refundModal.id);
        if (fresh.some(d => ['open', 'appealed'].includes(d.status))) {
          setRefundModal(null);
          setDetail(prev => (prev?.id === refundModal.id ? { ...prev, status: 'disputed' } : prev));
          toast.error(t('admin.rentals.refundBlockedByDispute'));
          loadRentals();
          return;
        }
      }
      toast.error(err.error || t('admin.rentals.refundFailed'));
    } finally {
      endAction();
    }
  };

  // Writing a set off. This is the only action in the panel that keeps a
  // customer's money for good, so the modal states the total she will have paid
  // (forfeit + late fees already charged) rather than only the forfeit — that
  // total is the number she can see on her card statement.
  const handleDeclareLost = async () => {
    if (!lostModal) return;
    if (!lostReason.trim()) return toast.error(t('admin.rentals.lostReasonRequired'));
    if (!beginAction()) return;
    try {
      // An empty box means "all of what is left of the deposit", which is what
      // the server defaults to. Sending 0 would mean forfeit nothing and refund
      // the lot — the opposite action.
      const body = { reason: lostReason.trim() };
      if (String(lostForfeit).trim() !== '') body.forfeit_amount = parseFloat(lostForfeit) || 0;

      const result = await api.post(`/rentals/${lostModal.id}/declare-lost`, body);
      toast.success(t('admin.rentals.lostDoneToast', {
        amount: (parseFloat(result.forfeited) || 0).toFixed(2),
      }), { duration: 8000 });
      setLostModal(null);
      setLostReason('');
      setLostForfeit('');
      setDetail(prev => (prev?.id === result.rental?.id ? { ...prev, ...result.rental } : prev));
      loadDisputes(result.rental.id);
      loadRentals();
    } catch (err) {
      toast.error(serverText(err, t('admin.rentals.lostFailed')), { duration: 10000 });
    } finally {
      endAction();
    }
  };

  // Refunding the rental price. Until this endpoint existed the only refund in
  // the API returned the deposit, so a booking cancelled before collection —
  // which the Terms promise back in full — had to be refunded by hand in the
  // Stripe dashboard, leaving the database still believing the rental was live
  // and the product still rented.
  const handleRefundRental = async () => {
    if (!rentalRefundModal) return;
    if (!beginAction()) return;
    try {
      const body = {};
      // An empty box is not zero: omitting `amount` is the normal, full-refund
      // case, and sending 0 would ask for a refund of nothing.
      if (String(rentalRefundAmount).trim() !== '') body.amount = parseFloat(rentalRefundAmount) || 0;
      if (rentalRefundReason.trim()) body.reason = rentalRefundReason.trim();

      const result = await api.post(`/rentals/${rentalRefundModal.id}/refund-rental`, body);
      const refunded = parseFloat(result.refunded) || 0;
      const depositPortion = parseFloat(result.deposit_portion) || 0;

      toast.success(
        t(depositPortion > 0 ? 'admin.rentals.rentalRefundedWithDepositToast' : 'admin.rentals.rentalRefundedToast', {
          amount: refunded.toFixed(2),
          deposit: depositPortion.toFixed(2),
        }),
        { duration: 10000 }
      );
      if (result.product_released) {
        toast.success(t('admin.rentals.productReleasedToast'), { duration: 10000 });
      }

      setDetail(prev => (prev?.id === rentalRefundModal.id ? { ...prev, ...result.rental } : prev));
      setRentalRefundModal(null);
      setRentalRefundAmount('');
      setRentalRefundReason('');
      loadRentals();
    } catch (err) {
      // 409 NOTHING_TO_REFUND / PAYMENT_CHARGEBACK_OPEN / already refunded, and
      // 400 REFUND_EXCEEDS_PAYMENT, all carry a message naming the obstacle or
      // the maximum. A generic "Failed" would send the admin back to Stripe.
      toast.error(serverText(err, t('admin.rentals.rentalRefundFailed')), { duration: 10000 });
    } finally {
      endAction();
    }
  };

  const isOverdue = (r) => r.due_at && new Date(r.due_at) < new Date() && !['returned', 'cancelled', 'disputed'].includes(r.status);

  // ── Dispute state for the open detail modal ──────────────────
  const liveDispute     = disputes.find(d => ['open', 'appealed'].includes(d.status)) || null;
  const resolvedDispute = !liveDispute
    ? (disputes.find(d => ['upheld', 'waived'].includes(d.status)) || null)
    : null;
  // What refund-deposit will apply if the deduction box is left empty. It
  // defaults server-side to COALESCE(amount_final, amount_claimed), so the UI
  // must read amount_final for an upheld dispute — reading amount_claimed showed
  // the original claim (€80) when only €30 was upheld and invited the operator
  // to over-withhold €50 of the customer's deposit (follow-up audit H1).
  const agreedDeduction = resolvedDispute
    ? (resolvedDispute.status === 'upheld'
        ? (Number.isFinite(parseFloat(resolvedDispute.amount_final))
            ? parseFloat(resolvedDispute.amount_final)
            : parseFloat(resolvedDispute.amount_claimed) || 0)
        : 0)
    : null;
  const effectiveDeduction = String(deduction).trim() === ''
    ? (agreedDeduction || 0)
    : parseFloat(deduction) || 0;

  const transitions    = (detail && STATUS_TRANSITIONS[detail.status]) || [];
  const canRefund      = !!detail && detail.status === 'returned' && !detail.deposit_refunded_at;
  // Anything the customer actually paid for and has not been given back yet.
  // Deliberately not restricted by status: a mid-rental goodwill refund is a
  // real case, and the server is the authority on what is still refundable.
  const canRefundRental = !!detail && !!detail.rental_paid_at && !detail.rental_refunded_at;
  const canOpenDispute = !!detail && ['returned', 'late'].includes(detail.status)
    && !detail.deposit_refunded_at && !liveDispute;
  // A set that was collected, is past due and has not been written off yet. The
  // server enforces all of this again, plus the customer's reply window, which
  // the UI cannot decide on its own — it depends on the live claim.
  const canDeclareLost = !!detail
    && ['late', 'disputed', 'picked_up'].includes(detail.status)
    && !detail.declared_lost_at
    && !detail.deposit_refunded_at
    && !detail.payment_disputed_at
    && !!detail.due_at && new Date(detail.due_at) < new Date();
  const detailDeposit  = detail ? parseFloat(detail.deposit_amount) || 0 : 0;

  // ── What the rental refund is about to do, worked out before the click ──
  // The server is the authority on the final figure; this is what it will do in
  // the ordinary case, spelled out so the admin is not moving money blind.
  const rr             = rentalRefundModal;
  // NET of the discount — total_rental_price is gross, but the server charged,
  // and refunds, total_rental_price − discount_amount. Showing the gross figure
  // as "Rental paid" and as the refund estimate overstated it by the discount
  // (a WELCOME20 rental shown as €90 paid when €72 was charged), so an operator
  // who typed the displayed amount dipped into the deposit (follow-up audit H2).
  const rrRentalPaid   = rr ? Math.max(0, (parseFloat(rr.total_rental_price) || 0) - (parseFloat(rr.discount_amount) || 0)) : 0;
  const rrDepositHeld  = rr && !rr.deposit_refunded_at ? parseFloat(rr.deposit_amount) || 0 : 0;
  const rrPartial      = String(rentalRefundAmount).trim() !== '';
  // Omitting the amount on a rental still in 'paid' is the full-cancellation
  // path: rental + deposit back, booking cancelled, product re-shelved.
  const rrFullCancel   = !!rr && rr.status === 'paid' && !rrPartial;
  const rrEstimate     = rrPartial
    ? (parseFloat(rentalRefundAmount) || 0)
    : rrRentalPaid + (rrFullCancel ? rrDepositHeld : 0);

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px' }}>
        <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, marginBottom: 4 }}>{t('admin.rentals.title')}</h1>
        <p style={{ color: theme.grey400, marginBottom: 32 }}>{t('admin.rentals.subtitle')}</p>

        {/* Search + status filters */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 28, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '0 0 280px' }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: theme.grey400 }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('admin.rentals.searchPlaceholder')}
              style={{ width: '100%', padding: '10px 12px 10px 34px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 13, fontFamily: theme.font.body, boxSizing: 'border-box', background: theme.white }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.grey400, padding: 0 }}>
                <X size={14} />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['all', 'paid', 'picked_up', 'returned', 'late', 'pending_payment', 'cancelled', 'disputed', 'lost'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                padding: '8px 16px', border: statusFilter === s ? 'none' : `1px solid ${theme.sandMid}`,
                background: statusFilter === s ? theme.ink : 'transparent',
                color: statusFilter === s ? theme.white : theme.grey600,
                borderRadius: 100, fontSize: 11, letterSpacing: '0.06em', cursor: 'pointer', fontFamily: theme.font.body, whiteSpace: 'nowrap'
              }}>
                {s === 'all' ? t('admin.status.all') : statusLabel(t, s)}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflow: 'hidden' }}>
          {/* totalCount, not the page length: the count is the whole match set
              across every page, which is what the admin is actually asking. */}
          {debouncedSearch && !loading && (
            <div style={{ padding: '10px 16px', background: theme.cream, borderBottom: `1px solid ${theme.creamBone}`, fontSize: 12, color: theme.grey500 }}>
              {t('admin.rentals.searchResults', { count: totalCount, query: debouncedSearch })}
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: theme.cream }}>
                  {['colCustomer', 'colProduct', 'colDays', 'colRental', 'colDeposit', 'colStatus', 'colDue', 'colNotes', ''].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 11, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{h ? t(`admin.rentals.${h}`) : ''}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={{ padding: 48, textAlign: 'center', color: theme.gold, fontFamily: theme.font.display, fontSize: 20 }}>{t('admin.rentals.loading')}</td></tr>
                ) : rentals.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: 48, textAlign: 'center', color: theme.grey400 }}>{t('admin.rentals.noRentals')}</td></tr>
                ) : rentals.map(r => {
                  const cfg = STATUS_COLORS[r.status] || { bg: theme.grey100, color: theme.grey600 };
                  const overdue = isOverdue(r);
                  return (
                    <tr key={r.id} onClick={() => openDetail(r)} style={{ borderBottom: `1px solid ${theme.creamDeep}`, cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = theme.cream}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '14px' }}>
                        <div style={{ fontWeight: 500, color: theme.ink }}>{r.full_name || '—'}</div>
                        <div style={{ color: theme.grey400, fontSize: 11 }}>{r.email}</div>
                        {/* The number the customer is calling from while standing
                            at the locker — it is searchable now, so show it. */}
                        {r.phone && <div style={{ color: theme.grey400, fontSize: 11 }}>{r.phone}</div>}
                      </td>
                      <td style={{ padding: '14px', color: theme.grey600 }}>{r.product_name}</td>
                      <td style={{ padding: '14px', color: theme.grey600 }}>{t('admin.rentals.daysShort', { count: r.rental_days })}</td>
                      <td style={{ padding: '14px', fontFamily: theme.font.display, fontSize: 18 }}>
                        €{Math.max(0, parseFloat(r.total_rental_price) - parseFloat(r.discount_amount || 0)).toFixed(0)}
                        {r.discount_code && <div style={{ fontSize: 10, color: theme.green, letterSpacing: '0.04em' }}>-{r.discount_code}</div>}
                      </td>
                      <td style={{ padding: '14px' }}>
                        <div style={{ fontFamily: theme.font.display, fontSize: 16 }}>€{parseFloat(r.deposit_amount).toFixed(0)}</div>
                        {r.deposit_refunded_at
                          ? <div style={{ color: theme.green, fontSize: 10, marginTop: 2 }}>{t('admin.rentals.depositRefundedTag')}</div>
                          : r.rental_paid_at && <div style={{ color: theme.amber, fontSize: 10, marginTop: 2 }}>{t('admin.rentals.depositHeldTag')}</div>}
                      </td>
                      <td style={{ padding: '14px' }}>
                        <span style={{ background: overdue ? theme.redWash : cfg.bg, color: overdue ? theme.redDark : cfg.color, padding: '3px 10px', borderRadius: 100, fontSize: 10, letterSpacing: '0.05em', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {overdue ? t('admin.status.overdue') : statusLabel(t, r.status)}
                        </span>
                        {r.late_fee > 0 && <div style={{ fontSize: 10, color: theme.red, marginTop: 3 }}>{t('admin.rentals.lateFeeTag', { amount: parseFloat(r.late_fee).toFixed(0) })}</div>}
                      </td>
                      <td style={{ padding: '14px', color: overdue ? theme.red : theme.grey400, fontSize: 12 }}>
                        {r.due_at ? format(new Date(r.due_at), 'MMM d') : '—'}
                      </td>
                      <td style={{ padding: '14px' }}>
                        {r.admin_notes && (
                          <span title={r.admin_notes} style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: theme.gold }} />
                        )}
                      </td>
                      <td style={{ padding: '14px', color: theme.gold }}>
                        <ChevronRight size={16} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, fontSize: 13, color: theme.grey500 }}>
            <span>
              {t('admin.rentals.pagination', {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, totalCount),
                total: totalCount,
              })}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.rentals.prev')}
              </button>
              <span style={{ padding: '8px 12px', background: theme.ink, color: theme.white, borderRadius: 4, fontSize: 12 }}>
                {page} / {Math.ceil(totalCount / PAGE_SIZE)}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * PAGE_SIZE >= totalCount}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page * PAGE_SIZE >= totalCount ? 'not-allowed' : 'pointer', opacity: page * PAGE_SIZE >= totalCount ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.rentals.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Detail Modal ───────────────────────────────────────── */}
      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.55), zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) closeDetail(); }}>
          <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: 820, maxHeight: '92vh', overflow: 'auto' }}>

            {/* Header */}
            <div style={{ padding: '28px 32px 20px', borderBottom: `1px solid ${theme.creamBone}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ fontFamily: theme.font.display, fontSize: 26, margin: '0 0 4px' }}>{detail.product_name}</h2>
                <div style={{ color: theme.grey600, fontSize: 14 }}>
                  {detail.full_name} · <span style={{ color: theme.grey400 }}>{detail.email}</span>
                  {detail.phone && <> · <span style={{ color: theme.grey400 }}>{detail.phone}</span></>}
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <StatusBadge status={detail.status} isOverdue={isOverdue(detail)} />
                  <span style={{ color: theme.gold, fontSize: 11, fontFamily: theme.font.mono }}>#{detail.id?.slice(0, 8)}</span>
                </div>
              </div>
              <button onClick={closeDetail} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.grey400, padding: 4 }}><X size={20} /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>

              {/* Left: Info */}
              <div style={{ padding: '24px 32px', borderRight: `1px solid ${theme.creamBone}` }}>

                {/* Pricing */}
                <Section title={t('admin.rentals.sectionPricing')}>
                  <Row label={t('admin.rentals.rowRentalDays')} value={t('admin.rentals.valueDays', { count: detail.rental_days })} />
                  <Row label={t('admin.rentals.rowBasePrice')} value={`€${parseFloat(detail.base_price || 0).toFixed(2)}`} />
                  {parseFloat(detail.extra_days_fee) > 0 && <Row label={t('admin.rentals.rowExtraDaysFee')} value={`€${parseFloat(detail.extra_days_fee).toFixed(2)}`} />}
                  {detail.discount_code && <Row label={t('admin.rentals.rowDiscount', { code: detail.discount_code })} value={`-€${parseFloat(detail.discount_amount || 0).toFixed(2)}`} valueColor={theme.green} />}
                  {/* NET of the discount, so Base − Discount actually adds up to the Total. */}
                  <Row label={t('admin.rentals.rowTotalRental')} value={`€${Math.max(0, parseFloat(detail.total_rental_price) - parseFloat(detail.discount_amount || 0)).toFixed(2)}`} bold />
                  <Row label={t('admin.rentals.rowDeposit')} value={`€${parseFloat(detail.deposit_amount).toFixed(2)}`} />
                  {parseFloat(detail.late_fee) > 0 && <Row label={t('admin.rentals.rowLateFee')} value={`€${parseFloat(detail.late_fee).toFixed(2)}`} valueColor={theme.red} />}
                  {detail.deposit_refunded_at && <Row label={t('admin.rentals.rowDepositRefunded')} value={`€${parseFloat(detail.deposit_refund_amount || detail.deposit_amount).toFixed(2)}`} valueColor={theme.green} />}
                  {/* A forfeited deposit is money KEPT, so it is shown apart
                      from the refunded figure above — they are opposite
                      directions and the same rental can have both. */}
                  {detail.deposit_forfeited_at && <Row label={t('admin.rentals.rowDepositForfeited')} value={`€${parseFloat(detail.deposit_forfeited_amount || 0).toFixed(2)}`} valueColor={theme.ink} bold />}
                  {detail.rental_refunded_at && <Row label={t('admin.rentals.rowRentalRefunded')} value={`€${(parseFloat(detail.rental_refund_amount ?? detail.total_rental_price) || 0).toFixed(2)}`} valueColor={theme.green} />}
                </Section>

                {/* Dates */}
                <Section title={t('admin.rentals.sectionTimeline')}>
                  <Row label={t('admin.rentals.rowCreated')}   value={detail.created_at  ? format(new Date(detail.created_at),  'dd MMM yyyy, HH:mm') : '—'} />
                  <Row label={t('admin.rentals.rowPaid')}      value={detail.rental_paid_at ? format(new Date(detail.rental_paid_at), 'dd MMM yyyy, HH:mm') : '—'} />
                  <Row label={t('admin.rentals.rowStarts')}    value={detail.starts_at   ? format(new Date(detail.starts_at),   'dd MMM yyyy') : '—'} />
                  <Row label={t('admin.rentals.rowDue')}       value={detail.due_at      ? format(new Date(detail.due_at),      'dd MMM yyyy') : '—'} valueColor={isOverdue(detail) ? theme.red : undefined} />
                  <Row label={t('admin.rentals.rowPickedUp')}  value={detail.picked_up_at ? format(new Date(detail.picked_up_at), 'dd MMM yyyy, HH:mm') : '—'} />
                  <Row label={t('admin.rentals.rowReturned')}  value={detail.returned_at ? format(new Date(detail.returned_at), 'dd MMM yyyy, HH:mm') : '—'} />
                  {/* The date late fees stopped, which is the first question a
                      customer asks about a write-off. */}
                  {detail.presumed_lost_at && <Row label={t('admin.rentals.rowPresumedLost')} value={format(new Date(detail.presumed_lost_at), 'dd MMM yyyy')} valueColor={theme.red} />}
                  {detail.declared_lost_at && <Row label={t('admin.rentals.rowDeclaredLost')} value={format(new Date(detail.declared_lost_at), 'dd MMM yyyy')} valueColor={theme.ink} />}
                </Section>

                {/* Locker */}
                {(detail.locker_id || detail.locker_location) && (
                  <Section title={t('admin.rentals.sectionLocker')}>
                    {detail.locker_id && <Row label={t('admin.rentals.rowLockerId')} value={detail.locker_id} />}
                    {detail.locker_location && <Row label={t('admin.rentals.rowLocation')} value={detail.locker_location} />}
                    {detail.pickup_box_name && <Row label={t('admin.rentals.rowBox')} value={detail.pickup_box_name} />}
                  </Section>
                )}

                {/* Bestwond 6-digit codes */}
                {(detail.pickup_take_code || detail.return_save_code) && (
                  <Section title={t('admin.rentals.sectionLockerCodes')}>
                    {detail.pickup_take_code && <Row label={t('admin.rentals.rowPickupTake')} value={<span style={{ fontFamily: theme.font.mono, fontSize: 15, letterSpacing: '0.1em' }}>{detail.pickup_take_code}</span>} />}
                    {detail.pickup_save_code && <Row label={t('admin.rentals.rowPickupSave')} value={<span style={{ fontFamily: theme.font.mono, fontSize: 15, letterSpacing: '0.1em' }}>{detail.pickup_save_code}</span>} />}
                    {detail.return_save_code && <Row label={t('admin.rentals.rowReturnSave')} value={<span style={{ fontFamily: theme.font.mono, fontSize: 15, letterSpacing: '0.1em' }}>{detail.return_save_code}</span>} />}
                    {detail.return_take_code && <Row label={t('admin.rentals.rowReturnTake')} value={<span style={{ fontFamily: theme.font.mono, fontSize: 15, letterSpacing: '0.1em' }}>{detail.return_take_code}</span>} />}
                  </Section>
                )}
              </div>

              {/* Right: Actions + Notes */}
              <div style={{ padding: '24px 32px' }}>

                {/* Status actions */}
                <Section title={t('admin.rentals.sectionActions')}>
                  {/* The refund and dispute buttons used to sit inside the
                      "has transitions" branch, which meant a `returned` rental —
                      the only status that can be refunded — showed nothing at all. */}
                  {!transitions.length && !canRefund && !canOpenDispute && !canRefundRental && !canDeclareLost ? (
                    <p style={{ color: theme.grey400, fontSize: 13 }}>{t('admin.rentals.noTransitions')}</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {transitions.map(s => (
                        <button key={s} disabled={actionLoading} onClick={() => updateStatus(detail.id, s)} style={{ padding: '10px 16px', background: TRANSITION_COLORS[s], color: theme.white, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body, textAlign: 'left', opacity: actionLoading ? 0.6 : 1 }}>
                          {t(`admin.rentals.${TRANSITION_LABEL_KEYS[s]}`)}
                        </button>
                      ))}

                      {canOpenDispute && (
                        <button
                          disabled={actionLoading}
                          onClick={() => {
                            setDisputeModal(detail);
                            setDisputeReason(detail.damage_description || '');
                            setDisputeAmount('');
                          }}
                          style={{ padding: '10px 16px', background: theme.purple, color: theme.white, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6, opacity: actionLoading ? 0.6 : 1 }}>
                          <AlertTriangle size={13} /> {t('admin.rentals.openDispute')}
                        </button>
                      )}

                      {canRefundRental && (
                        <button
                          disabled={actionLoading}
                          onClick={() => { setRentalRefundModal(detail); setRentalRefundAmount(''); setRentalRefundReason(''); }}
                          style={{ padding: '10px 16px', background: theme.blue, color: theme.white, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6, opacity: actionLoading ? 0.6 : 1 }}>
                          <DollarSign size={13} /> {t('admin.rentals.refundRental')}
                        </button>
                      )}

                      {canDeclareLost && (
                        <button
                          disabled={actionLoading}
                          onClick={() => {
                            setLostModal(detail);
                            setLostReason(detail.damage_description || '');
                            setLostForfeit('');
                          }}
                          style={{ padding: '10px 16px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6, opacity: actionLoading ? 0.6 : 1 }}>
                          <AlertTriangle size={13} /> {t('admin.rentals.declareLost')}
                        </button>
                      )}

                      {canRefund && (
                        <button onClick={() => { setRefundModal(detail); setDeduction(agreedDeduction === null ? '0' : ''); setDeductionReason(''); }} style={{ padding: '10px 16px', background: theme.amber, color: theme.white, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <DollarSign size={13} /> {t('admin.rentals.processRefund')}
                        </button>
                      )}
                    </div>
                  )}
                </Section>

                {/* Admin notes */}
                <Section title={t('admin.rentals.sectionNotes')}>
                  <label style={labelStyle}>{t('admin.rentals.internalNote')}</label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={3}
                    placeholder={t('admin.rentals.internalNotePlaceholder')}
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 13, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical', marginBottom: 12 }}
                  />

                  <label style={labelStyle}>{t('admin.rentals.damageDescription')}</label>
                  <textarea
                    value={damage}
                    onChange={e => setDamage(e.target.value)}
                    rows={3}
                    placeholder={t('admin.rentals.damagePlaceholder')}
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 13, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical', marginBottom: 12 }}
                  />

                  <button onClick={saveNotes} disabled={actionLoading} style={{ width: '100%', padding: '12px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: actionLoading ? 0.6 : 1, fontFamily: theme.font.body }}>
                    <Save size={13} /> {t('admin.rentals.saveNotes')}
                  </button>
                </Section>
              </div>
            </div>

            {/* ── Dispute ─────────────────────────────────────── */}
            {(liveDispute || resolvedDispute) && (() => {
              const d = liveDispute || resolvedDispute;
              const claimed = parseFloat(d.amount_claimed) || 0;
              // For a resolved dispute the governing figure is the FINAL agreed
              // amount, not the original claim (see H1 above); for a live one it
              // is still the claim.
              const agreed = resolvedDispute
                ? (resolvedDispute.status === 'upheld'
                    ? (Number.isFinite(parseFloat(d.amount_final)) ? parseFloat(d.amount_final) : claimed)
                    : 0)
                : claimed;
              const accent = liveDispute ? theme.purple : d.status === 'upheld' ? theme.red : theme.green;
              return (
                <div style={{ borderTop: `1px solid ${theme.creamBone}`, padding: '24px 32px 32px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <AlertTriangle size={15} color={accent} />
                    <span style={{ fontSize: 11, color: theme.grey400, letterSpacing: '0.1em' }}>{t('admin.rentals.sectionDispute')}</span>
                    <span style={{ background: `${accent}15`, color: accent, padding: '3px 12px', borderRadius: 100, fontSize: 10, letterSpacing: '0.05em', fontWeight: 500 }}>
                      {t(`admin.rentals.${DISPUTE_STATUS_KEYS[d.status]}`)}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
                    <div>
                      <Row label={t('admin.rentals.disputeReason')} value={d.reason} />
                      <Row
                        label={liveDispute ? t('admin.rentals.disputeClaimed') : t('admin.rentals.disputeAgreed')}
                        value={`€${agreed.toFixed(2)}`}
                        valueColor={agreed > 0 ? theme.red : theme.green}
                      />
                      {resolvedDispute && agreed !== claimed && (
                        <Row label={t('admin.rentals.disputeOriginallyClaimed')} value={`€${claimed.toFixed(2)}`} />
                      )}
                      <Row label={t('admin.rentals.rowDeposit')} value={`€${detailDeposit.toFixed(2)}`} />
                      <Row label={t('admin.rentals.disputeOpenedAt')} value={format(new Date(d.created_at), 'dd MMM yyyy, HH:mm')} />
                      {liveDispute && (
                        <Row
                          label={t('admin.rentals.disputeDeadline')}
                          value={format(new Date(d.appeal_deadline), 'dd MMM yyyy, HH:mm')}
                          valueColor={new Date(d.appeal_deadline) <= new Date() ? theme.grey400 : theme.amber}
                        />
                      )}
                      {d.resolved_at && <Row label={t('admin.rentals.disputeResolvedAt')} value={format(new Date(d.resolved_at), 'dd MMM yyyy, HH:mm')} />}
                      {d.resolution_notes && <Row label={t('admin.rentals.disputeResolutionNotes')} value={d.resolution_notes} />}
                    </div>

                    <div>
                      <label style={labelStyle}>{t('admin.rentals.disputeCustomerResponse')}</label>
                      <div style={{ background: theme.cream, border: `1px solid ${theme.creamBone}`, borderRadius: 4, padding: 14, fontSize: 13, color: d.customer_response ? theme.ink : theme.grey400, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                        {d.customer_response || t('admin.rentals.disputeNoResponse')}
                        {d.customer_responded_at && (
                          <div style={{ color: theme.grey400, fontSize: 11, marginTop: 8 }}>
                            {format(new Date(d.customer_responded_at), 'dd MMM yyyy, HH:mm')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Evidence. Shown for a resolved dispute too — read-only,
                      because that is the file you have to produce months later
                      if the customer's bank asks. Only a live dispute can take
                      new photos; the API refuses the rest. */}
                  <div style={{ marginTop: 20, borderTop: `1px solid ${theme.creamBone}`, paddingTop: 18 }}>
                    <label style={labelStyle}>{t('admin.rentals.evidenceLabel')}</label>

                    {d.evidence_urls?.length > 0 ? (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0 12px' }}>
                        {d.evidence_urls.map((u, i) => (
                          // Opened full size in a tab: a thumbnail of a damaged
                          // weft proves nothing at 80px.
                          <a key={i} href={photoUrl(u)} target="_blank" rel="noreferrer noopener">
                            <img src={photoUrl(u)} alt=""
                              style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: `1px solid ${theme.sand}`, display: 'block' }} />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12.5, color: theme.grey400, margin: '8px 0 12px' }}>
                        {t('admin.rentals.evidenceNone')}
                      </div>
                    )}

                    {liveDispute && (
                      <>
                        <label style={{
                          display: 'inline-flex', alignItems: 'center', gap: 7,
                          padding: '9px 18px', border: `1px solid ${theme.sand}`, background: theme.white,
                          borderRadius: 4, cursor: evidenceUploading ? 'wait' : 'pointer',
                          opacity: evidenceUploading ? 0.6 : 1, fontSize: 12, color: theme.grey600,
                        }}>
                          <Upload size={13} />
                          {evidenceUploading ? t('admin.rentals.evidenceUploading') : t('admin.rentals.evidenceAdd')}
                          <input
                            type="file" accept="image/*" multiple hidden
                            disabled={evidenceUploading}
                            onChange={e => {
                              handleEvidenceUpload(detail.id, e.target.files);
                              // Cleared so re-picking the same file fires change
                              // again after a failed upload.
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <div style={{ fontSize: 11, color: theme.grey400, marginTop: 8, lineHeight: 1.6 }}>
                          {t('admin.rentals.evidenceHelp', { max: MAX_EVIDENCE_PHOTOS })}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Resolve controls */}
                  {liveDispute && (
                    <div style={{ marginTop: 24, borderTop: `1px solid ${theme.creamBone}`, paddingTop: 20 }}>
                      <label style={labelStyle}>{t('admin.rentals.disputeFinalAmount')}</label>
                      <input
                        type="number" min="0" max={detailDeposit} step="0.01"
                        value={resolveFinal}
                        onChange={e => setResolveFinal(e.target.value)}
                        placeholder={claimed.toFixed(2)}
                        style={{ width: 180, padding: '10px 12px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 15, boxSizing: 'border-box' }}
                      />
                      <div style={{ fontSize: 11, color: theme.grey400, marginTop: 6, marginBottom: 14 }}>
                        {t('admin.rentals.disputeFinalAmountHelp', { deposit: detailDeposit.toFixed(2), claimed: claimed.toFixed(2) })}
                      </div>

                      <label style={labelStyle}>{t('admin.rentals.disputeResolutionNotes')}</label>
                      <textarea
                        value={resolveNotes}
                        onChange={e => setResolveNotes(e.target.value)}
                        rows={2}
                        placeholder={t('admin.rentals.disputeResolutionNotesPlaceholder')}
                        style={{ width: '100%', padding: '10px 12px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 13, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical', marginBottom: 14 }}
                      />

                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <button disabled={actionLoading} onClick={() => handleResolve('upheld')}
                          style={{ padding: '12px 24px', background: theme.red, color: theme.white, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body, opacity: actionLoading ? 0.6 : 1 }}>
                          {t('admin.rentals.disputeUphold')}
                        </button>
                        <button disabled={actionLoading} onClick={() => handleResolve('waived')}
                          style={{ padding: '12px 24px', background: theme.green, color: theme.white, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body, opacity: actionLoading ? 0.6 : 1 }}>
                          {t('admin.rentals.disputeWaive')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Resolved — point at the refund, which is where the money moves */}
                  {resolvedDispute && !detail.deposit_refunded_at && (
                    <div style={{ marginTop: 20, background: theme.creamDeep, border: `1px solid ${theme.sandDeep}`, borderRadius: 4, padding: 18 }}>
                      <div style={{ fontFamily: theme.font.display, fontSize: 18, color: theme.goldInk, marginBottom: 8 }}>
                        {t('admin.rentals.disputeNextStepTitle')}
                      </div>
                      <p style={{ color: theme.goldMoss, fontSize: 13, lineHeight: 1.7, margin: '0 0 14px' }}>
                        {t('admin.rentals.disputeNextStepBody', { amount: (agreedDeduction || 0).toFixed(2) })}
                      </p>
                      <button onClick={() => { setRefundModal(detail); setDeduction(''); setDeductionReason(resolvedDispute.resolution_notes || resolvedDispute.reason || ''); }}
                        style={{ padding: '11px 24px', background: theme.amber, color: theme.white, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <DollarSign size={13} /> {t('admin.rentals.processRefund')}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Open-a-dispute Modal ───────────────────────────────── */}
      {disputeModal && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.6), zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: 520, padding: 40 }}>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 28, marginBottom: 8 }}>{t('admin.rentals.disputeModalTitle')}</h2>
            <p style={{ color: theme.grey500, fontSize: 14, marginBottom: 24 }}>
              {disputeModal.product_name} · {disputeModal.full_name || disputeModal.email}<br />
              <span style={{ fontSize: 13 }}>{t('admin.rentals.depositHeld')} <strong>€{parseFloat(disputeModal.deposit_amount).toFixed(2)}</strong></span>
            </p>

            <p style={{ background: theme.creamDeep, border: `1px solid ${theme.sandDeep}`, borderRadius: 4, padding: 14, color: theme.goldMoss, fontSize: 12, lineHeight: 1.7, marginBottom: 20 }}>
              {t('admin.rentals.disputeModalNotice')}
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>{t('admin.rentals.disputeReason')}</label>
              <textarea
                value={disputeReason}
                onChange={e => setDisputeReason(e.target.value)}
                rows={4}
                placeholder={t('admin.rentals.disputeReasonPlaceholder')}
                style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>{t('admin.rentals.disputeAmountLabel')}</label>
              <input
                type="number" min="0" max={disputeModal.deposit_amount} step="0.01"
                value={disputeAmount}
                onChange={e => setDisputeAmount(e.target.value)}
                placeholder="0.00"
                style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 16, boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: 11, color: theme.grey400, marginTop: 6, lineHeight: 1.6 }}>
                {t('admin.rentals.disputeAmountHelp', { deposit: parseFloat(disputeModal.deposit_amount).toFixed(2) })}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setDisputeModal(null)} style={{ flex: 1, padding: '14px', background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 2, cursor: 'pointer', fontSize: 13, fontFamily: theme.font.body }}>{t('admin.rentals.cancel')}</button>
              <button onClick={handleOpenDispute} disabled={actionLoading || !disputeReason.trim()}
                style={{ flex: 2, padding: '14px', background: theme.purple, color: theme.white, border: 'none', borderRadius: 2, cursor: actionLoading || !disputeReason.trim() ? 'not-allowed' : 'pointer', opacity: actionLoading || !disputeReason.trim() ? 0.6 : 1, fontSize: 13, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
                {actionLoading ? t('admin.rentals.processing') : t('admin.rentals.disputeSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Refund-the-rental-price Modal ──────────────────────── */}
      {rr && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.6), zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: 520, padding: 40, maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 28, marginBottom: 8 }}>{t('admin.rentals.rentalRefundTitle')}</h2>
            <p style={{ color: theme.grey500, fontSize: 14, marginBottom: 20 }}>
              {rr.product_name} · {rr.full_name || rr.email}<br />
              <span style={{ fontSize: 13 }}>{t('admin.rentals.rentalPaidLabel')} <strong>€{rrRentalPaid.toFixed(2)}</strong></span>
            </p>

            {/* Stated before the click, not after: this moves real money out of
                the Stripe balance and there is no undo. */}
            <div style={{ background: theme.creamDeep, border: `1px solid ${theme.sandDeep}`, borderRadius: 4, padding: 16, marginBottom: 20 }}>
              <p style={{ color: theme.goldMoss, fontSize: 13, lineHeight: 1.75, margin: 0 }}>
                {rrFullCancel
                  ? t('admin.rentals.rentalRefundFullNotice', {
                      total: rrEstimate.toFixed(2),
                      rental: rrRentalPaid.toFixed(2),
                      deposit: rrDepositHeld.toFixed(2),
                    })
                  : rrPartial
                    ? t('admin.rentals.rentalRefundPartialNotice', { amount: rrEstimate.toFixed(2) })
                    : t('admin.rentals.rentalRefundPriceOnlyNotice', { amount: rrRentalPaid.toFixed(2) })}
              </p>
              <p style={{ color: theme.redDark, fontSize: 12, lineHeight: 1.7, margin: '10px 0 0', fontWeight: 500 }}>
                {t('admin.rentals.rentalRefundIrreversible')}
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>{t('admin.rentals.rentalRefundAmountLabel')}</label>
              <input
                type="number" min="0" step="0.01"
                value={rentalRefundAmount}
                onChange={e => setRentalRefundAmount(e.target.value)}
                placeholder={rrRentalPaid.toFixed(2)}
                style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 16, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: theme.grey400, marginTop: 6, lineHeight: 1.6 }}>
                {t('admin.rentals.rentalRefundAmountHelp')}
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>{t('admin.rentals.rentalRefundReasonLabel')}</label>
              <textarea
                value={rentalRefundReason}
                onChange={e => setRentalRefundReason(e.target.value)}
                rows={3}
                placeholder={t('admin.rentals.rentalRefundReasonPlaceholder')}
                style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical' }} />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setRentalRefundModal(null)} style={{ flex: 1, padding: '14px', background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 2, cursor: 'pointer', fontSize: 13, fontFamily: theme.font.body }}>{t('admin.rentals.cancel')}</button>
              <button onClick={handleRefundRental} disabled={actionLoading}
                style={{ flex: 2, padding: '14px', background: theme.blue, color: theme.white, border: 'none', borderRadius: 2, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.6 : 1, fontSize: 13, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
                {actionLoading ? t('admin.rentals.processing') : t('admin.rentals.rentalRefundConfirm', { amount: rrEstimate.toFixed(2) })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Refund Modal ───────────────────────────────────────── */}
      {refundModal && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.6), zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: 480, padding: 40 }}>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 28, marginBottom: 8 }}>{t('admin.rentals.refundTitle')}</h2>
            <p style={{ color: theme.grey500, fontSize: 14, marginBottom: 24 }}>
              {refundModal.product_name} · {refundModal.full_name || refundModal.email}<br />
              <span style={{ fontSize: 13 }}>{t('admin.rentals.depositHeld')} <strong>€{parseFloat(refundModal.deposit_amount).toFixed(2)}</strong></span>
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>{t('admin.rentals.deductionLabel')}</label>
              <input type="number" min="0" max={refundModal.deposit_amount} step="0.01" value={deduction} onChange={e => setDeduction(e.target.value)}
                placeholder={agreedDeduction === null ? '0.00' : agreedDeduction.toFixed(2)}
                style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 16, boxSizing: 'border-box' }} />
              {agreedDeduction !== null && (
                <div style={{ fontSize: 11, color: theme.goldInk, marginTop: 6, lineHeight: 1.6 }}>
                  {t('admin.rentals.deductionAgreedHint', { amount: agreedDeduction.toFixed(2) })}
                </div>
              )}
            </div>

            <div style={{ background: theme.creamDeep, padding: 16, borderRadius: 4, marginBottom: 16, fontSize: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{t('admin.rentals.fullDeposit')}</span><span>€{parseFloat(refundModal.deposit_amount).toFixed(2)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: theme.red }}><span>{t('admin.rentals.deduction')}</span><span>-€{effectiveDeduction.toFixed(2)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, borderTop: `1px solid ${theme.sand}`, marginTop: 8, paddingTop: 8 }}>
                <span>{t('admin.rentals.refundAmount')}</span>
                <span style={{ color: theme.green }}>€{Math.max(0, parseFloat(refundModal.deposit_amount) - effectiveDeduction).toFixed(2)}</span>
              </div>
            </div>

            {effectiveDeduction > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{t('admin.rentals.reasonLabel')}</label>
                <textarea value={deductionReason} onChange={e => setDeductionReason(e.target.value)} rows={3} placeholder={t('admin.rentals.reasonPlaceholder')} style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box' }} />
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setRefundModal(null)} style={{ flex: 1, padding: '14px', background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 2, cursor: 'pointer', fontSize: 13, fontFamily: theme.font.body }}>{t('admin.rentals.cancel')}</button>
              <button onClick={handleRefund} disabled={actionLoading} style={{ flex: 2, padding: '14px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, cursor: 'pointer', fontSize: 13, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
                {actionLoading ? t('admin.rentals.processing') : t('admin.rentals.confirmRefund')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Declare lost ─────────────────────────────────────── */}
      {lostModal && (() => {
        const remaining = Math.max(0, (parseFloat(lostModal.deposit_amount) || 0) - (parseFloat(lostModal.deposit_refund_amount) || 0));
        const forfeit = String(lostForfeit).trim() === ''
          ? remaining
          : Math.min(Math.max(0, parseFloat(lostForfeit) || 0), remaining);
        const lateCharged = parseFloat(lostModal.late_fee_charged) || 0;
        return (
          <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.6), zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: 520, padding: 40 }}>
              <h2 style={{ fontFamily: theme.font.display, fontSize: 28, marginBottom: 8 }}>{t('admin.rentals.lostTitle')}</h2>
              <p style={{ color: theme.grey500, fontSize: 14, marginBottom: 20 }}>
                {lostModal.product_name} · {lostModal.full_name || lostModal.email}
              </p>

              <div style={{ background: theme.creamDeep, padding: 16, borderRadius: 4, marginBottom: 20, fontSize: 13, lineHeight: 1.7 }}>
                {t('admin.rentals.lostExplain')}
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{t('admin.rentals.lostForfeitLabel')}</label>
                <input type="number" min="0" max={remaining} step="0.01" value={lostForfeit}
                  onChange={e => setLostForfeit(e.target.value)}
                  placeholder={remaining.toFixed(2)}
                  style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 16, boxSizing: 'border-box' }} />
                <div style={{ fontSize: 11, color: theme.grey500, marginTop: 6, lineHeight: 1.6 }}>
                  {t('admin.rentals.lostForfeitHint', { amount: remaining.toFixed(2) })}
                </div>
              </div>

              {/* The total is the point of this box. The operator is deciding how
                  much this loss costs the customer, and the forfeit alone does
                  not answer that — late fees were already taken from the same
                  card before the meter stopped. */}
              <div style={{ background: theme.creamDeep, padding: 16, borderRadius: 4, marginBottom: 16, fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{t('admin.rentals.lostForfeited')}</span><span>€{forfeit.toFixed(2)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: theme.grey500 }}><span>{t('admin.rentals.lostLateFees')}</span><span>€{lateCharged.toFixed(2)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: theme.green }}><span>{t('admin.rentals.lostRefunded')}</span><span>€{(remaining - forfeit).toFixed(2)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, borderTop: `1px solid ${theme.sand}`, marginTop: 8, paddingTop: 8 }}>
                  <span>{t('admin.rentals.lostTotalRecovered')}</span>
                  <span>€{(forfeit + lateCharged).toFixed(2)}</span>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>{t('admin.rentals.lostReasonLabel')}</label>
                <textarea value={lostReason} onChange={e => setLostReason(e.target.value)} rows={3}
                  placeholder={t('admin.rentals.lostReasonPlaceholder')}
                  style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setLostModal(null)} style={{ flex: 1, padding: '14px', background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 2, cursor: 'pointer', fontSize: 13, fontFamily: theme.font.body }}>{t('admin.rentals.cancel')}</button>
                <button onClick={handleDeclareLost} disabled={actionLoading} style={{ flex: 2, padding: '14px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, cursor: 'pointer', fontSize: 13, letterSpacing: '0.1em', fontFamily: theme.font.body, opacity: actionLoading ? 0.6 : 1 }}>
                  {actionLoading ? t('admin.rentals.processing') : t('admin.rentals.lostConfirm')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────

const labelStyle = { display: 'block', fontSize: 11, color: theme.grey500, letterSpacing: '0.08em', marginBottom: 6 };

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, color: theme.grey400, letterSpacing: '0.1em', marginBottom: 10, borderBottom: `1px solid ${theme.creamBone}`, paddingBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, bold, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, fontSize: 13 }}>
      <span style={{ color: theme.grey500 }}>{label}</span>
      <span style={{ color: valueColor || theme.ink, fontWeight: bold ? 600 : 400, fontFamily: bold ? theme.font.display : undefined, fontSize: bold ? 18 : undefined }}>{value}</span>
    </div>
  );
}

function StatusBadge({ status, isOverdue }) {
  const { t } = useTranslation();
  const cfg = STATUS_COLORS[status] || { bg: theme.grey100, color: theme.grey600 };
  return (
    <span style={{ background: isOverdue ? theme.redWash : cfg.bg, color: isOverdue ? theme.redDark : cfg.color, padding: '3px 12px', borderRadius: 100, fontSize: 11, letterSpacing: '0.05em', fontWeight: 500 }}>
      {isOverdue ? t('admin.status.overdue') : statusLabel(t, status)}
    </span>
  );
}

